import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Agent, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { shareReplay, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class AgentService extends HateoasResourceOperation<Agent> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    model: Agent | null = null;
    // Every frontdoor page plus the side nav and list header ask for the account on
    // each navigation, which was several identical in-flight requests per route
    // change. Short TTL so ops-side changes (feature flags, version) still surface
    // without a reload.
    private static readonly ACCOUNT_CACHE_TTL_MS = 30_000;
    private accountCache = new Map<string, { at: number; request: Observable<any> }>();
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(Agent);
        this.apiUrl = this.environment.frontdoorURL;

    }


    public findAgents(options?: PagedGetOption): Promise<Agent[]> {
        return super
            .getPage({ ...AgentService.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((agents) => (agents ? agents.resources : []));
    }


    public getEnrollmentToken( id: string, orgId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';

        const fullpath = `${this.apiUrl}${version}/${id}/agents/enrollment`;
        return this.http
            .post(fullpath, {organizationId: orgId})
    }

    public pingAgent( frontdoorId: string, id:string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/agents/${id}/version`;
            return this.http
                .get(fullpath, {headers: headers})
    }


    public delete(frontdoorId: string, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/environments/${id}`;
            return this.http
                .delete(fullpath, {headers: headers})
    }
    

    public getAccountById(orgId: string) {
        const cached = this.accountCache.get(orgId);
        if (cached && Date.now() - cached.at < AgentService.ACCOUNT_CACHE_TTL_MS) {
            return cached.request;
        }
        const fullpath = `${this.apiUrl}/v2/organization/${orgId}`
        const request = this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
            .pipe(
                catchError((error) => {
                    // Don't hold a failed response for the rest of the TTL.
                    this.accountCache.delete(orgId);
                    return observableThrowError(() => error);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        this.accountCache.set(orgId, { at: Date.now(), request });
        return request;
    }

    public clearAccountCache(orgId?: string) {
        if (orgId) {
            this.accountCache.delete(orgId);
        } else {
            this.accountCache.clear();
        }
    }

    public createAccount(orgId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version};`
        return this.http
            .post(fullpath, {organizationId: orgId})
            .pipe(tap(() => this.clearAccountCache(orgId)));
    }

    public getAgents(isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/agents`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

        public getSingleAgent(id, zId, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${id}/environments/${zId}`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }
    
    public getAgentsByEnv(frontdoorId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const remoteAgent = isV2 ? '?remoteAgent=true' : ''
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/environments${remoteAgent}`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }
    
    public getMetricsPerFrontdoor(frontdoorId: string, isV2: boolean, duration = '24h') {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/metrics?duration=${encodeURIComponent(duration)}`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerShare(frontdoorId: string, id: string, isV2: boolean, duration = '24h') {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/shares/${id}/metrics?duration=${encodeURIComponent(duration)}`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerHttpShare(frontdoorId: string, id: string, duration = '24h') {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/http-shares/${id}/metrics?duration=${encodeURIComponent(duration)}`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerEnv(frontdoorId: string, envId: string, isV2: boolean, duration = '24h') {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/environments/${envId}/metrics?duration=${encodeURIComponent(duration)}`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getHealthChecks(frontdoorId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/health-checks`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }}) 
    }

    public getSparklines(frontdoorId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/sparklines`
        return this.http
        .get(fullpath,{
    headers: { Accept: 'application/hal+json' }})
    }


    public getModel(): Agent | null {
            return this.model;
        }
        
    public setModel(data: Agent) {
            this.model = data;
        }


    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

                  return headers.append('Content-Type', 'application/json');
    }

        /**
     * Generic error handler for bad requests
     */
        protected handleError(scope: any, error: HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred. Handle it accordingly.
                scope.logger.error('Feature Service error occurred:', error.error);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
            }
            scope.lastErrorSource.next(error);
            return observableThrowError(error);
        }
}
